const ALLOWED_TAGS = new Set([
  "a",
  "blockquote",
  "br",
  "caption",
  "col",
  "colgroup",
  "div",
  "em",
  "figcaption",
  "figure",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "img",
  "li",
  "ol",
  "p",
  "hr",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
]);

const GLOBAL_ATTRS = new Set(["class", "id", "title"]);
const TAG_ATTRS = {
  a: new Set(["href", "rel", "target"]),
  img: new Set(["src", "alt", "width", "height", "loading", "crossorigin"]),
  sup: new Set(["data-footnote-id"]),
  td: new Set(["colspan", "rowspan"]),
  th: new Set(["colspan", "rowspan"]),
  table: new Set(["style"]),
  td_style: new Set(["style"]),
  th_style: new Set(["style"]),
  col_style: new Set(["style"]),
  img_style: new Set(["style"]),
};

function isAllowedAttr(tag, name) {
  if (name.startsWith("data-")) return true;
  if (GLOBAL_ATTRS.has(name)) return true;
  if (TAG_ATTRS[tag]?.has(name)) return true;
  if ((tag === "td" || tag === "th") && TAG_ATTRS[`${tag}_style`]?.has(name)) return true;
  if (tag === "col" && TAG_ATTRS.col_style?.has(name)) return true;
  if (tag === "img" && TAG_ATTRS.img_style?.has(name)) return true;
  return false;
}

function isSafeUrl(value) {
  const trimmed = value.trim().toLowerCase();
  if (trimmed.startsWith("javascript:")) return false;
  if (trimmed.startsWith("data:")) {
    return trimmed.startsWith("data:image/");
  }
  if (!trimmed.includes(":")) {
    return true;
  }
  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("./") ||
    trimmed.startsWith("../") ||
    trimmed.startsWith("blob:")
  );
}

function sanitizeStyle(tag, styleValue) {
  const allowedPropsByTag = {
    table: new Set(["width", "table-layout"]),
    td: new Set(["padding", "padding-left", "padding-right", "padding-top", "padding-bottom", "line-height", "text-align", "vertical-align"]),
    th: new Set(["padding", "padding-left", "padding-right", "padding-top", "padding-bottom", "line-height", "text-align", "vertical-align"]),
    col: new Set(["width"]),
    img: new Set(["width", "height"]),
  };
  const allowedProps = allowedPropsByTag[tag] || new Set(["width"]);
  const safePairs = [];

  styleValue.split(";").forEach((pair) => {
    const [rawProp, ...rawValParts] = pair.split(":");
    if (!rawProp || rawValParts.length === 0) return;
    const prop = rawProp.trim().toLowerCase();
    const value = rawValParts.join(":").trim();
    if (!allowedProps.has(prop)) return;
    if (/url\s*\(/i.test(value)) return;
    if (/expression\s*\(/i.test(value)) return;
    if (prop === "width" && !/^[0-9.]+(px|%|mm|cm|in)?$/i.test(value)) return;
    if (prop === "table-layout" && !/^(auto|fixed)$/i.test(value)) return;
    if (prop.startsWith("padding") && !/^[0-9.]+(px|pt|mm|cm|in)?(\s+[0-9.]+(px|pt|mm|cm|in)?){0,3}$/i.test(value)) return;
    if (prop === "line-height" && !/^[0-9.]+(px|pt)?$/i.test(value)) return;
    if (prop === "text-align" && !/^(left|right|center|justify)$/i.test(value)) return;
    if (prop === "vertical-align" && !/^(top|middle|bottom|baseline)$/i.test(value)) return;
    safePairs.push(`${prop}: ${value}`);
  });

  if (safePairs.length === 0) return "";
  return safePairs.join("; ");
}

export function sanitizeHtml(input) {
  if (!input || typeof input !== "string") return "";
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return input;
  }

  const doc = new DOMParser().parseFromString(`<div>${input}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return "";

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);

  while (walker.nextNode()) {
    const el = walker.currentNode;
    const tag = el.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      const text = doc.createTextNode(el.textContent || "");
      el.parentNode?.replaceChild(text, el);
      continue;
    }

    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
        return;
      }
      if (!isAllowedAttr(tag, name)) {
        el.removeAttribute(attr.name);
        return;
      }
      if (name === "style") {
        const sanitized = sanitizeStyle(tag, attr.value);
        if (sanitized) {
          el.setAttribute("style", sanitized);
        } else {
          el.removeAttribute(attr.name);
        }
        return;
      }
      if ((name === "href" || name === "src") && !isSafeUrl(attr.value)) {
        el.removeAttribute(attr.name);
      }
    });
  }

  return root.innerHTML;
}
